import { useEffect, useState } from "react";

import { getDriverAdminMonitoring } from "../../api/client.js";
import { Icon } from "../Icon.jsx";

const PHASE_LABELS = {
  NOT_STARTED: "Belum dimulai",
  PREPARATION: "Persiapan",
  LOADING: "Proses loading",
  READY_TO_DEPART: "Siap berangkat",
  IN_PROGRESS: "Perjalanan berjalan",
  RETURNING: "Kembali ke gudang",
  POST_TRIP_CHECK: "Pemeriksaan akhir",
  CLOSED: "Selesai",
};

function formatCurrency(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) return "—";

  const parsed = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Jakarta",
  }).format(parsed);
}

function normalizePhaseClass(value) {
  return String(value || "NOT_STARTED")
    .trim()
    .toLowerCase()
    .replaceAll("_", "-");
}

export function SedulurDalanMonitoringPanel({ fleetOrderId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  async function load() {
    setLoading(true);
    setError("");

    try {
      const payload = await getDriverAdminMonitoring(fleetOrderId);
      setData(payload.fleetOrders?.[0] || null);
      setRefreshedAt(new Date());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [fleetOrderId]);

  if (loading && !data) {
    return (
      <div className="fleet-workbench-empty">
        <span className="loading-spinner" /> Memuat monitoring Sedulur Dalan...
      </div>
    );
  }

  if (error && !data) {
    return <div className="error-banner">{error}</div>;
  }

  if (!data) {
    return <div className="fleet-workbench-empty">Data eksekusi driver belum tersedia.</div>;
  }

  const progress = data.totalStops
    ? Math.round((data.completedStops / data.totalStops) * 100)
    : 0;
  const inspection = data.inspection || {};
  const phaseClass = normalizePhaseClass(data.operationalPhase);

  return (
    <section className="driver-admin-monitoring">
      {data.criticalIncidents > 0 && (
        <div className="driver-admin-alert">
          <Icon name="warning" size={20} />
          <div>
            <strong>Kendala kritis memerlukan respons</strong>
            <span>{data.criticalIncidents} laporan High/Darurat masih terbuka.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner" role="alert">
          Data terakhir tetap ditampilkan. Pembaruan gagal: {error}
        </div>
      )}

      <header className="driver-admin-monitoring__header">
        <div>
          <span className={`driver-status-chip driver-status-chip--${phaseClass}`}>
            {PHASE_LABELS[data.operationalPhase] || data.operationalPhase}
          </span>
          <h3>Progres Sedulur Dalan</h3>
          <p>Aktivitas driver tersimpan terpisah dari data perencanaan Fleet Order.</p>
          {refreshedAt && (
            <small className="driver-admin-monitoring__updated">
              Diperbarui {formatDateTime(refreshedAt)}
            </small>
          )}
        </div>

        <button
          className="secondary-button"
          disabled={loading}
          onClick={load}
          title={loading ? "Data sedang diperbarui." : "Perbarui monitoring driver."}
          type="button"
        >
          <Icon name="refresh" size={17} />
          {loading ? "Memperbarui…" : "Perbarui"}
        </button>
      </header>

      <div className="driver-admin-kpis">
        <article>
          <span>Progres titik</span>
          <strong>{data.completedStops}/{data.totalStops}</strong>
          <small>{progress}% selesai</small>
        </article>
        <article>
          <span>Titik bermasalah</span>
          <strong>{data.problemStops}</strong>
          <small>Gagal atau ditunda</small>
        </article>
        <article>
          <span>Hari perjalanan</span>
          <strong>{data.tripDayCount}</strong>
          <small>Sesi perjalanan</small>
        </article>
        <article>
          <span>Total pengeluaran</span>
          <strong>{formatCurrency(data.expenseTotalIdr)}</strong>
          <small>Termasuk seluruh pengajuan</small>
        </article>
      </div>

      <div
        aria-label={`Progres perjalanan ${progress}%`}
        className="driver-admin-progress"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={progress}
      >
        <span style={{ width: `${progress}%` }} />
      </div>

      <div className="driver-admin-monitoring__grid">
        <dl>
          <div><dt>Driver</dt><dd>{data.driverName || "—"}</dd></div>
          <div><dt>Co-driver</dt><dd>{data.coDriverName || "—"}</dd></div>
          <div><dt>Kendaraan</dt><dd>{data.vehiclePlate || "—"}</dd></div>
          <div><dt>Titik aktif</dt><dd>{data.activeStopKey || "Tidak ada"}</dd></div>
        </dl>
        <dl>
          <div><dt>Inspeksi sebelum perjalanan</dt><dd>{formatDateTime(inspection.preTripAt)}</dd></div>
          <div><dt>Inspeksi setelah perjalanan</dt><dd>{formatDateTime(inspection.postTripAt)}</dd></div>
          <div>
            <dt>Odometer akhir</dt>
            <dd>
              {data.finalOdometerKm === null || data.finalOdometerKm === undefined
                ? "—"
                : `${Number(data.finalOdometerKm).toLocaleString("id-ID")} km`}
            </dd>
          </div>
          <div><dt>Aktivitas terakhir</dt><dd>{formatDateTime(data.lastActivityAt)}</dd></div>
        </dl>
      </div>

      {data.openIncidents > 0 && (
        <div className="logistics-warning">
          <strong>{data.openIncidents} kendala belum ditangani</strong>
          <span>Buka monitoring operasional untuk menindaklanjuti laporan driver.</span>
        </div>
      )}
    </section>
  );
}
